import React from "react";
import Box from "@mui/material/Box";
import Solution from "./Solution";
import Score from "./Score4c";
import Reattempt from "./Reattempt";
import Questions4c from "./Questions4c";
import FeedbackCard from "./FeedbackCard";
import Review4cHeader from "./Review4cHeader";

import { Session } from "utils";
import { useParams, useSearchParams } from "react-router-dom";
import { useServices } from 'contexts/SingleSessionProvider';
import { useSnackbar } from 'contexts/SnackbarProvider';
import { get4cFeedbackByToken, getCodeChallengeAttempts } from "services";
import { Break, WhiteBox } from "components/AppShell";
import { Categories, CodeComplexity, SubCategoryWeightages } from './constants';
import { Skeleton } from "@mui/material";
import { useNavigateWithClient } from "hooks";

const PerformanceReview4c = () => {
  const { aid } = useParams();
  const [searchParams] = useSearchParams();

  const service = useServices();
  const snackbar = useSnackbar();
  const navigate = useNavigateWithClient();

  const [loading, setLoading] = React.useState(true);
  const [currentAttempt, setCurrentAttempt] = React.useState(null);
  const [challenge, setChallenge] = React.useState(null);
  const [followups, setFollowups] = React.useState([]);
  const [codeSubmissions, setCodeSubmissions] = React.useState([]);
  const [analysis, setAnalysis] = React.useState([]);
  const [details, setDetails] = React.useState(null);
  const [myScore, setMyScore] = React.useState(1);
  const [categories, setCategories] = React.useState(Categories);
  const [proctorRecords, setProctorRecords] = React.useState([]);
  const [entryPoint, setEntryPoint] = React.useState({});
  const [isGuestApp, setIsGuestApp] = React.useState(false);

  React.useEffect(() => {
    if (aid) {
      setCurrentAttempt(aid);
    }
  }, [aid]);

  React.useEffect(() => {
    if (currentAttempt) {
      getAttempt(currentAttempt);
    }
  }, [currentAttempt]);

  React.useEffect(() => {
    const at = searchParams.get('at');
    if (!at) return;

    // Session.accessToken = at;
    setIsGuestApp(true);

    setLoading(true);
    service.callService(get4cFeedbackByToken, at)
      .then(codeChallengeAttempt => {
        const {
          codeChallenges: [codeChallenge], finishedAt, startedAt, codeSubmissions, analysis,
          followUps, feedback, proctorRecords, assessmentEntryPoint
        } = codeChallengeAttempt;

        setDetails({ codeChallenge, finishedAt, startedAt, codeSubmissions });
        setChallenge(codeChallenge);
        setFollowups(followUps);
        setCodeSubmissions(codeSubmissions);
        setAnalysis(analysis);
        setProctorRecords(proctorRecords);
        calculateMyScore(feedback);
        curateDetailedFeedback(feedback);
        setEntryPoint(assessmentEntryPoint);

      }).catch(error => {
        console.error(error);
        snackbar.error("Uh Oh! Unable to get your Performance analysis");
      }).finally(() => {
        setLoading(false);
      });
  }, []);

  const getAttempt = React.useCallback(async (attemptId) => {

    setLoading(true);

    const filters = {
      _id: attemptId, user: Session.userId, includeTotal: true,
      include: ["codeChallenges"]
    };

    service.callService(getCodeChallengeAttempts, filters)
      .then(({ total, codeChallengeAttempts }) => {
        if (!total) throw new Error("Details not found!");

        const {
          codeChallenges: [codeChallenge], finishedAt, startedAt, codeSubmissions, analysis,
          followUps, feedback, proctorRecords, assessmentEntryPoint
        } = codeChallengeAttempts[0];

        setDetails({ codeChallenge, finishedAt, startedAt, codeSubmissions });
        setChallenge(codeChallenge);
        setFollowups(followUps);
        setCodeSubmissions(codeSubmissions);
        setAnalysis(analysis);
        setProctorRecords(proctorRecords);
        calculateMyScore(feedback);
        curateDetailedFeedback(feedback);
        setEntryPoint(assessmentEntryPoint);
      })
      .catch((error) => {
        console.error(error);
        snackbar.error("Uh Oh! Unable to get details. Please try again.", false);
        navigate("/personalized-interview?type=coding");
      })
      .finally(() => setLoading(false));
  }, []);

  // calculating overall score 
  const calculateMyScore = React.useCallback((feedback) => {

    const total = SubCategoryWeightages.reduce((prev, curr) => {
      const { subCategory, weightage } = curr;
      return prev + ((feedback[subCategory].score * weightage) / 100);
    }, 0);

    //Calculates the average and rounding the result to two decimal places
    const avg = Math.round(total * 100) / 100;

    setMyScore(avg < 1 ? 1 : avg);
  }, []);

  const curateDetailedFeedback = React.useCallback((feedback) => {

    const newCategories = categories.map((category) => {
      const { subCategories } = category;
      const newSubCategories = [];

      const total = subCategories.reduce((prev, curr) => {
        const { weightage, id } = curr;

        if (id === 'SCALABILITY_AND_EFFICIENCY') {
          Object.keys(CodeComplexity).forEach((key) => {
            if (feedback?.[id]?.[key])
              feedback[id]?.improvementTips?.push(`${CodeComplexity[key]} - ${feedback[id][key]}`);
          });
        }

        newSubCategories.push({ ...curr, ...feedback[id] });
        return prev + ((feedback[id].score * weightage) / 100);
      }, 0);

      //Calculates the average and rounding the result to two decimal places
      const avg = Math.round(total * 100) / 100;
      let score = category.id === 'speech' ? avg : ((avg < 1) ? 1 : avg);

      return { ...category, score: score, subCategories: newSubCategories };
    });

    setCategories(newCategories);
  }, []);

  return (
    <Box width='100%' display='flex' flexDirection='column' gap='16px'>

      <Review4cHeader
        currentAttempt={currentAttempt}
        setCurrentAttempt={setCurrentAttempt}
        details={details}
        isGuestApp={isGuestApp}
      />

      {
        loading ?
          <Skeleton variant='rectangular' width={'100%'} height={300} /> :
          <>
            <WhiteBox>
              <Score score={myScore} proctorRecords={proctorRecords} />

              <Break value={5} />

              <Questions4c challenge={challenge} followUps={followups} />

              <Break value={5} />

              <Solution challenge={challenge} codeSubmissions={codeSubmissions} analysis={analysis} />
            </WhiteBox>

            <WhiteBox>

              <FeedbackCard categories={categories} />

            </WhiteBox>
          </>
      }

      {
        entryPoint?.data !== 'contest' && !isGuestApp && <Reattempt challenge={challenge} />
      }
    </Box>

  )
}

export default PerformanceReview4c